import { getSorensonEndpoint } from 'pages/JoinInterpreter/helpers';
import React, {
  FC,
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import useSocket from 'utils/hooks/useSocket';
import { useAuth } from './auth-context';
import regAxios from 'axios';
import { UserType } from 'utils/constants';

interface IMeetingParticipant {
  uuid: string;
  user_id: string;
}
interface IMeetingInfo {
  joinUrl: string;
  userId: string;
}

interface IMeetingStatus {
  isInMeeting: boolean;
  isHost: boolean | null;
  isInterpreterConnected: boolean | null;
}

interface IInterpreterRequest {
  loading: boolean;
  error: boolean;
  inProcess: boolean;
  callId: string;
  errorMessage?: string;
}

interface IMeetingStatusContext extends IMeetingStatus {
  currentMeeting: IMeetingInfo;
  interpreterRequest: IInterpreterRequest;
  initialLoad: boolean;
  setInterpreterRequest: (data: IInterpreterRequest) => void;
  setCurrentMeeting: (data: IMeetingInfo) => void;
  getInterpreterStatus: (callId: string) => any;
  resetInterpreterRequest: () => void;
}

export const MeetingStatusContext = createContext<IMeetingStatusContext>({
  isInMeeting: false,
  isHost: null,
  isInterpreterConnected: null,
  currentMeeting: { joinUrl: '', userId: '' },
  interpreterRequest: {
    loading: false,
    error: false,
    inProcess: false,
    callId: '',
  },
  initialLoad: true,
  setInterpreterRequest: (data) => console.log(data),
  setCurrentMeeting: (data: IMeetingInfo) => console.log(data),
  getInterpreterStatus: () => '',
  resetInterpreterRequest: () => {},
});

export const MeetingStatusContextProvider: FC = ({ children }) => {
  const { sorensonUser, userZoomData, axios, getTokenSilently } = useAuth();

  const { socket } = useSocket(userZoomData.accountId);

  const [meetingStatus, setMeetingStatus] = useState<IMeetingStatus>({
    isInMeeting: false,
    isHost: null,
    isInterpreterConnected: null,
  });

  const [interpreterRequest, setInterpreterRequest] =
    useState<IInterpreterRequest>({
      loading: false,
      error: false,
      inProcess: false,
      callId: '',
    });

  const [currentMeeting, setCurrentMeeting] = useState<IMeetingInfo>({
    joinUrl: '',
    userId: '',
  });

  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  const [requestQueue, setRequestQueue] = useState<string[]>([]);

  const resetInterpreterRequest = () => {
    setInterpreterRequest({
      loading: false,
      error: false,
      inProcess: false,
      callId: '',
      errorMessage: undefined,
    });
    setMeetingStatus({
      ...meetingStatus,
      isInterpreterConnected: false,
    });
  };

  const getLiveMeeting = useCallback(async (): Promise<any | null> => {
    const { data } = await axios.get<any>('live');
    const [meeting] = data.meetings;
    return meeting || null;
  }, [axios]);

  const onParticipantJoined = useCallback(
    async ({ object }: any) => {
      const { user_id, id } = object.participant;

      if (id === userZoomData.id) {
        const { join_url } = (await getLiveMeeting()) || {};
        setMeetingStatus({
          isInMeeting: true,
          isHost: !!join_url,
          isInterpreterConnected: meetingStatus.isInterpreterConnected,
        });
        setCurrentMeeting({
          joinUrl: join_url as string,
          userId: user_id,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getLiveMeeting, interpreterRequest.callId, meetingStatus, userZoomData]
  );

  const onParticipantLeft = useCallback(
    ({ object }: any) => {
      const { id } = object.participant;
      if (id === userZoomData.id) {
        setMeetingStatus({
          isInMeeting: false,
          isHost: null,
          isInterpreterConnected: false,
        });
        setCurrentMeeting({ joinUrl: '', userId: '' });
        setInterpreterRequest((request) => ({
          ...request,
          loading: false,
          error: false,
          inProcess: false,
          callId: request.callId,
        }));
        setRequestQueue((queue) => [...queue, interpreterRequest.callId]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meetingStatus, userZoomData]
  );

  const init = useCallback(async () => {
    const { join_url, uuid: meetingId } = (await getLiveMeeting()) || {};
    const { data } = await axios.get<IMeetingParticipant>(
      `participant/${encodeURIComponent(userZoomData.accountId)}`
    );
    const { user_id, uuid } = data;
    if (join_url) {
      setMeetingStatus({
        isInMeeting: !!join_url,
        isHost: !!join_url,
        isInterpreterConnected: false,
      });
      setCurrentMeeting({
        joinUrl: join_url,
        userId: uuid === meetingId ? user_id : '',
      });
    }
    setInitialLoad(false);
  }, [axios, getLiveMeeting, userZoomData.accountId]);

  const getInterpreterStatus = async (callId: string) => {
    const url = getSorensonEndpoint(sorensonUser.userType);
    const apiKey =
      sorensonUser.userType === UserType.VRI
        ? process.env.REACT_APP_API_KEY_VRI
        : process.env.REACT_APP_API_KEY_VRS;
    try {
      const { data } = await regAxios.get(`${url}callstatus/${callId}`, {
        headers: {
          'x-api-key': apiKey as string,
          'Content-Type': 'application/json',
          Authorization: await getTokenSilently(),
        },
      });
      if (data === 'Connected') {
        setMeetingStatus({
          ...meetingStatus,
          isInterpreterConnected: true,
        });
      }
      return data;
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (socket) {
      socket.on('meeting.participant_joined', onParticipantJoined);
      socket.on('meeting.participant_left', onParticipantLeft);
    }

    if (socket) {
      return () => {
        socket.off('meeting.participant_joined');
        socket.off('meeting.participant_left');
      };
    }
  }, [socket, onParticipantJoined, onParticipantLeft]);

  useEffect(() => {
    if (userZoomData.accountId) {
      init();
    }
  }, [userZoomData, init]);

  return (
    <MeetingStatusContext.Provider
      value={{
        isInMeeting: meetingStatus.isInMeeting,
        isHost: meetingStatus.isHost,
        isInterpreterConnected: meetingStatus.isInterpreterConnected,
        currentMeeting,
        interpreterRequest,
        initialLoad,
        setCurrentMeeting,
        setInterpreterRequest,
        getInterpreterStatus,
        resetInterpreterRequest,
      }}
    >
      {children}
    </MeetingStatusContext.Provider>
  );
};

export const useMeetingStatus = () => {
  return useContext(MeetingStatusContext);
};
